import type { EndpointsRegistry, FetchClient } from '../types'
import { GatewayEndpoints } from '../endpoints/gateway-endpoints'
import { mapBehaviour, type GroupBehaviourResponse } from '~/api/services/groups'
import type {
  Schedule,
  ScheduleAssetDTO,
  ScheduleData,
  ScheduleEvent,
  ScheduleEventDto,
  EventCreatePayload,
  UpdateSchedulePayload,
  SyncStatusFilter,
  CommStatusFilter
} from '~/types/schedule'
import type { PaginatedResult, PaginationParams, ScheduleQueryParams } from '~/types/pagination'
import type { SuccessNoContent } from '~/types/common'
import type { AssetCapabilities } from '~/types/capabilities'

// behaviours arrive snake_case, unlike the rest of the payload
type ScheduleByIdResponse = Omit<Schedule, 'behaviours'> & { behaviours?: GroupBehaviourResponse[] }

export interface ScheduleService {
  getSchedules: (pagination: ScheduleQueryParams) => Promise<PaginatedResult<Schedule>>
  getSchedulesByMonth: (year: number, month: number) => Promise<Schedule[]>
  getScheduleById: (id: number) => Promise<Schedule>
  createSchedule: (schedule: Partial<ScheduleData>) => Promise<Schedule>
  updateSchedule: (id: number, schedule: UpdateSchedulePayload) => Promise<Schedule>
  deleteSchedule: (id: number) => Promise<SuccessNoContent>
  getScheduleEventsByScheduleId: (
    scheduleId: number,
    pagination: PaginationParams
  ) => Promise<PaginatedResult<ScheduleEvent>>
  getScheduleEventsByMonth: (year: number, month: number, siteId?: number) => Promise<ScheduleEventDto[]>
  getScheduleEventById: (scheduleId: number, eventId: number) => Promise<ScheduleEventDto>
  getScheduleAssetsByScheduleId: (
    scheduleId: number,
    pagination: PaginationParams & {
      sync_status?: SyncStatusFilter[]
      comm_status?: CommStatusFilter[]
      group_ids?: string[]
      zone_ids?: string[]
    }
  ) => Promise<PaginatedResult<ScheduleAssetDTO>>
  getScheduleCapabilitiesByScheduleId: (scheduleId: number) => Promise<AssetCapabilities>
  createScheduleEvent: (scheduleId: number, payload: EventCreatePayload) => Promise<ScheduleEventDto>
  updateScheduleEvent: (scheduleId: number, eventId: number, payload: EventCreatePayload) => Promise<ScheduleEventDto>
  deleteScheduleEvent: (scheduleId: number, eventId: number) => Promise<void>
  removeAssetFromSchedule: (scheduleId: number, assetId: number) => Promise<void>
  retryFailedAssets: (scheduleId: number) => Promise<SuccessNoContent>
}

// const _mock = {
//   $schema: 'http://localhost:8080/schemas/PaginatedResultSchedule.json',
//   data: [
//     {
//       id: 157,
//       name: 'Test Schedule Assets #5',
//       status: 'draft',
//       color: '#FF0000',
//       createdAt: '2025-09-25T12:46:25.005182Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-25T12:46:25.005182Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 75
//       }
//     },
//     {
//       id: 153,
//       name: 'Test Schedule Group Assets #3',
//       status: 'published',
//       color: '#bbff00',
//       createdAt: '2025-09-25T12:41:16.091265Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-25T12:41:16.091265Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 100
//       }
//     },
//     {
//       id: 152,
//       name: 'Test Schedule Route Assets',
//       status: 'published',
//       color: '#FF0000',
//       createdAt: '2025-09-25T12:37:54.428361Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-25T12:37:54.428361Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 50,
//         UNKNOWN: 1
//       }
//     },
//     {
//       id: 151,
//       name: ' Test Schedule Zone Assets #2',
//       status: 'published',
//       color: '#00ff2a',
//       createdAt: '2025-09-25T12:32:17.787558Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-25T12:32:17.787558Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 529
//       }
//     },
//     {
//       id: 150,
//       name: ' Test Schedule Removed Assets',
//       status: 'published',
//       color: '#aeff00',
//       createdAt: '2025-09-24T23:20:43.42022Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-24T23:20:43.42022Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 30
//       }
//     },
//     {
//       id: 149,
//       name: ' Test Schedule Assets',
//       status: 'published',
//       color: '#0077cc',
//       createdAt: '2025-09-24T23:18:03.913483Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-25T12:28:02.348072Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 478,
//         UNKNOWN: 0
//       }
//     },
//     {
//       id: 148,
//       name: 'Test Schedule Zone Assets',
//       status: 'draft',
//       color: '#0095ff',
//       createdAt: '2025-09-24T23:10:15.761932Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-24T23:10:15.761932Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 10,
//         PROCESSING: 60,
//         SENDING: 10,
//         SENT: 10,
//         SUCCESS: 10,
//         UNKNOWN: 429
//       }
//     },
//     {
//       id: 146,
//       name: 'Test Schedule No Assets',
//       status: 'draft',
//       color: '#0A2538',
//       createdAt: '2025-09-24T13:01:38.39822Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-24T13:01:38.39822Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 0
//       }
//     },
//     {
//       id: 147,
//       name: 'Test Schedule Group Assets 2',
//       status: 'draft',
//       color: '#0A2538',
//       createdAt: '2025-09-24T13:01:38.39822Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-24T13:01:38.39822Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 0,
//         PROCESSING: 0,
//         SENDING: 0,
//         SENT: 0,
//         SUCCESS: 0,
//         UNKNOWN: 100
//       }
//     },
//     {
//       id: 145,
//       name: 'Test Schedule Group Assets',
//       status: 'published',
//       color: '#0A2538',
//       createdAt: '2025-09-24T12:49:11.529429Z',
//       createdBy: 'opoitras',
//       updatedAt: '2025-09-24T12:49:11.529429Z',
//       updatedBy: 'opoitras',
//       eventCount: 0,
//       assetsByStatus: {
//         ERROR: 5,
//         PROCESSING: 145,
//         SENDING: 25,
//         SENT: 3,
//         SUCCESS: 2,
//         UNKNOWN: 0
//       }
//     }
//   ],
//   pagination: {
//     per_page: 25,
//     current_page: 1,
//     sort: 'Id asc',
//     total: 14,
//     total_pages: 1
//   }
// }
// const _mockCapabilities = [
//   {
//     actionKey: 'updateLineMessage',
//     category: 'OP',
//     unsupportedAssetIds: [],
//     capabilities: [
//       {
//         nameKey: 'changePowerState',
//         parameters: [
//           {
//             nameKey: 'powerState',
//             type: 'string',
//             valueDomain: ['ON', 'OFF'],
//             affectedState: 'POWER_STATE',
//             maxValue: null,
//             minValue: null
//           }
//         ]
//       },
//       {
//         nameKey: 'displayScrollableText',
//         parameters: [
//           {
//             nameKey: 'text',
//             type: 'string',
//             affectedState: 'TEXT',
//             maxValue: null,
//             minValue: null,
//             charset: 'iso8859-1'
//           }
//         ]
//       },
//       {
//         nameKey: 'setDisplayBlinkingPattern',
//         parameters: []
//       }
//     ]
//   },
//   {
//     actionKey: 'fetchLog',
//     category: 'OP',
//     unsupportedAssetIds: [1],
//     capabilities: [
//       {
//         nameKey: 'fetchLog',
//         parameters: [
//           {
//             nameKey: 'category',
//             type: 'string',
//             valueDomain: ['STANDARD', 'OPERATIONAL', 'SECURITY', 'DIAGNOSTIC'],
//             affectedState: 'LOG_CATEGORY',
//             maxValue: null,
//             minValue: null
//           },
//           {
//             nameKey: 'seqNo',
//             type: 'number',
//             affectedState: 'SEQ_NO',
//             maxValue: null,
//             minValue: null
//           }
//         ]
//       }
//     ]
//   },
//   {
//     actionKey: 'fetchNextLog',
//     category: 'OP',
//     unsupportedAssetIds: [],
//     capabilities: [
//       {
//         nameKey: 'fetchNextLog',
//         parameters: [
//           {
//             nameKey: 'category',
//             type: 'string',
//             valueDomain: ['STANDARD', 'OPERATIONAL', 'SECURITY', 'DIAGNOSTIC'],
//             affectedState: 'LOG_CATEGORY',
//             maxValue: null,
//             minValue: null
//           },
//           {
//             nameKey: 'seqNo',
//             type: 'number',
//             affectedState: 'SEQ_NO',
//             maxValue: null,
//             minValue: null
//           }
//         ]
//       }
//     ]
//   }
// ]

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): ScheduleService => {
  return {
    getSchedules: async (pagination: ScheduleQueryParams): Promise<PaginatedResult<Schedule>> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_GET_ALL, {
        queryParams: {
          current_page: pagination.current_page,
          per_page: pagination.per_page,
          ...(pagination.sort && { sort: pagination.sort }),
          ...(pagination.filter && { filter: pagination.filter }),
          ...(pagination.search_terms && { search_terms: pagination.search_terms }),
          ...(pagination.site_id && { site_id: pagination.site_id })
        }
      })
      return await client<PaginatedResult<Schedule>>(url, { method, headers, body })
    },
    getSchedulesByMonth: async (year: number, month: number): Promise<Schedule[]> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_GET_BY_MONTH, {
        queryParams: {
          year,
          month
        }
      })
      return await client<Schedule[]>(url, { method, headers, body })
    },
    getScheduleById: async (id: number): Promise<Schedule> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_GET_BY_ID, {
        params: { id }
      })
      const { behaviours, ...rest } = await client<ScheduleByIdResponse>(url, { method, headers, body })
      return {
        ...rest,
        ...(behaviours && { behaviours: behaviours.map(mapBehaviour) })
      }
    },
    createSchedule: async (schedule: Partial<ScheduleData>): Promise<Schedule> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_CREATE, {}, schedule)
      return await client<Schedule>(url, { method, headers, body })
    },
    updateSchedule: async (id: number, schedule: UpdateSchedulePayload): Promise<Schedule> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.SCHEDULES_UPDATE,
        { params: { id } },
        schedule
      )
      return await client<Schedule>(url, { method, headers, body })
    },
    deleteSchedule: async (id: number): Promise<SuccessNoContent> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_DELETE, {
        params: { id }
      })
      return await client(url, { method, headers, body })
    },
    getScheduleEventsByScheduleId: async (
      scheduleId: number,
      pagination: PaginationParams
    ): Promise<PaginatedResult<ScheduleEvent>> => {
      const USE_MOCK = false
      if (USE_MOCK) {
        const { mockScheduleEvents } = await import('~/mocks/scheduleEventsMock')
        return Promise.resolve(mockScheduleEvents)
      }

      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.SCHEDULE_EVENTS_GET_BY_SCHEDULE_ID,
        {
          params: { scheduleId },
          queryParams: {
            current_page: pagination.current_page,
            per_page: pagination.per_page
          }
        }
      )
      return await client<PaginatedResult<ScheduleEvent>>(url, { method, headers, body })
    },
    getScheduleEventsByMonth: async (year: number, month: number, siteId?: number): Promise<ScheduleEventDto[]> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULE_EVENTS_GET_BY_MONTH, {
        params: {
          year,
          month
        },
        queryParams: {
          year,
          month,
          ...(siteId && { site_id: siteId })
        }
      })
      return await client<ScheduleEventDto[]>(url, { method, headers, body })
    },
    getScheduleEventById: async (scheduleId: number, eventId: number): Promise<ScheduleEventDto> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULE_EVENTS_GET_BY_ID, {
        params: { scheduleId, eventId }
      })
      return await client<ScheduleEventDto>(url, { method, headers, body })
    },
    getScheduleAssetsByScheduleId: async (
      scheduleId: number,
      pagination: PaginationParams & {
        sync_status?: SyncStatusFilter[]
        comm_status?: CommStatusFilter[]
        group_ids?: string[]
        zone_ids?: string[]
      }
    ): Promise<PaginatedResult<ScheduleAssetDTO>> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_GET_ASSETS_BY_ID, {
        params: {
          scheduleId
        },
        queryParams: {
          current_page: pagination.current_page,
          per_page: pagination.per_page,
          ...(pagination.sort && { sort: pagination.sort }),
          ...(pagination.sync_status?.length && { sync_status: pagination.sync_status.join(',') }),
          ...(pagination.comm_status?.length && { comm_status: pagination.comm_status.join(',') }),
          ...(pagination.group_ids?.length && { group_ids: pagination.group_ids.join(',') }),
          ...(pagination.zone_ids?.length && { zone_ids: pagination.zone_ids.join(',') })
        }
      })
      return await client<PaginatedResult<ScheduleAssetDTO>>(url, { method, headers, body })
    },
    getScheduleCapabilitiesByScheduleId: async (scheduleId: number): Promise<AssetCapabilities> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_GET_CAPABILITIES_BY_ID, {
        params: { scheduleId }
      })
      return await client<AssetCapabilities>(url, { method, headers, body })
    },
    createScheduleEvent: async (scheduleId: number, payload: EventCreatePayload): Promise<ScheduleEventDto> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.SCHEDULE_EVENTS_CREATE,
        {
          params: { scheduleId }
        },
        payload
      )
      return await client<ScheduleEventDto>(url, { method, headers, body })
    },
    updateScheduleEvent: async (
      scheduleId: number,
      eventId: number,
      payload: EventCreatePayload
    ): Promise<ScheduleEventDto> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.SCHEDULE_EVENTS_UPDATE,
        {
          params: { scheduleId, eventId }
        },
        payload
      )
      return await client<ScheduleEventDto>(url, { method, headers, body })
    },
    deleteScheduleEvent: async (scheduleId: number, eventId: number): Promise<void> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULE_EVENTS_DELETE, {
        params: { scheduleId, eventId }
      })
      await client(url, { method, headers, body })
    },
    removeAssetFromSchedule: async (scheduleId: number, assetId: number): Promise<void> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_REMOVE_ASSET, {
        params: { scheduleId, assetId }
      })
      await client(url, { method, headers, body })
    },
    retryFailedAssets: async (scheduleId: number): Promise<SuccessNoContent> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.SCHEDULES_RETRY_FAILED, {
        params: { id: scheduleId }
      })
      return await client<SuccessNoContent>(url, { method, headers, body })
    }
  }
}
